import { useEffect } from 'react';
import { useRouter } from 'next/router';

import { CARE_DATES, FLOWS, VERTICALS_NAMES } from '@/constants';
import { ServiceIdsForSeniorMember, TrafficSource, TrafficSourceType } from '@/types/seeker';
import getVertical from '@/utilities/verticalUtils';
import {
  AdditionalCareKind,
  CareKind,
  CareKindIntent,
  DefaultCareKind,
  ServiceIdsForMember,
} from '@/types/seekerCC';
import { ServiceTypes } from '@/types/seekerPC';
import { careKindIntentMap, careKindServiceIdMap, initialState } from '@/state/seekerCC/reducer';
import { useAppDispatch, useFlowState } from '../AppState';

const intentStringParam: { [key: string]: CARE_DATES } = {
  rightnow: CARE_DATES.RIGHT_NOW,
  withinweek: CARE_DATES.WITHIN_A_WEEK,
  withinmonth: CARE_DATES.IN_1_2_MONTHS,
  justbrowsing: CARE_DATES.JUST_BROWSING,
};

const subServicesStringParam: {
  [key: string]: DefaultCareKind | AdditionalCareKind | ServiceTypes;
} = {
  daycare: DefaultCareKind.DAY_CARE_CENTERS,
  nanny: DefaultCareKind.NANNIES_RECURRING_BABYSITTERS,
  babysitter: DefaultCareKind.ONE_TIME_BABYSITTERS,
  childcareDaycareBackup: AdditionalCareKind.CHILDCARE_DAYCARE_BACKUP,
  sitting: ServiceTypes.SITTING,
  boarding: ServiceTypes.BOARDING,
  grooming: ServiceTypes.GROOMING,
  training: ServiceTypes.TRAINING,
};

const sources = ['seo', 'sem', 'vhp', 'community'];
const ccSubServices = ['daycare', 'nanny', 'babysitter', 'childcareDaycareBackup'];
const pcSubServices = ['sitting', 'boarding', 'grooming', 'training'];

export default function useQueryParamData() {
  const dispatch = useAppDispatch();
  const { flowName } = useFlowState();
  const { query, pathname } = useRouter();

  const {
    intent,
    source,
    subService,
    zipcode,
    fromSeoPage,
    fromSemPage,
    serviceIdForMember,
    careKind,
    ib,
    utm_campaign: utmCampaign,
  } = query;
  const vertical = getVertical(pathname);

  useEffect(() => {
    let zipcodeSupplied = false;
    if (zipcode) {
      zipcodeSupplied = true;
      dispatch({ type: 'setZipcode', zipcode: zipcode as string });
      dispatch({ type: 'setLandingPageZipcode', landingPageZipcode: zipcode as string });
    } // should we set default value in else statement?

    if (source && sources.includes(source as string)) {
      if (flowName === FLOWS.SEEKER_CHILD_CARE.name) {
        dispatch({ type: 'cc_setEnrollmentSource', value: source as string });
      } else if (flowName === FLOWS.SEEKER_TUTORING.name) {
        dispatch({ type: 'tu_setEnrollmentSource', value: source as string });
      } else if (flowName === FLOWS.SEEKER_HOUSEKEEPING.name) {
        dispatch({ type: 'hk_setEnrollmentSource', value: source as string });
      } else if (flowName === FLOWS.SEEKER_PET_CARE.name) {
        dispatch({ type: 'pc_setEnrollmentSource', value: source as string });
      }
    }

    let intentSupplied = false;
    const intentString = intent && (intent as string).toLowerCase();
    if (intentString && Object.keys(intentStringParam).includes(intentString)) {
      if (flowName === FLOWS.SEEKER_CHILD_CARE.name) {
        intentSupplied = true;
        dispatch({ type: 'setCareDate', careDate: intentStringParam[intentString] as CARE_DATES });
      } else if (flowName === FLOWS.SEEKER_TUTORING.name) {
        intentSupplied = true;
        dispatch({
          type: 'setTutoringDate',
          careDate: intentStringParam[intentString] as CARE_DATES,
        });
      } else if (flowName === FLOWS.SEEKER_HOUSEKEEPING.name) {
        intentSupplied = true;
        dispatch({
          type: 'setHousekeepingDate',
          careDate: intentStringParam[intentString] as CARE_DATES,
        });
      } else if (flowName === FLOWS.SEEKER_PET_CARE.name) {
        intentSupplied = true;
        dispatch({
          type: 'setPetCareDate',
          careDate: intentStringParam[intentString] as CARE_DATES,
        });
      }
    }

    let careKindSupplied = false;
    const subServiceString = subService && (subService as string).toLowerCase();
    if (subServiceString) {
      if (flowName === FLOWS.SEEKER_CHILD_CARE.name && ccSubServices.includes(subServiceString)) {
        careKindSupplied = true;
        dispatch({
          type: 'setCareKind',
          careKind: subServicesStringParam[subServiceString] as CareKind,
        });
      } else if (
        flowName === FLOWS.SEEKER_PET_CARE.name &&
        pcSubServices.includes(subServiceString)
      ) {
        careKindSupplied = true;
        dispatch({
          type: 'setServiceType',
          serviceType: subServicesStringParam[subServiceString] as ServiceTypes,
        });
      }
    }

    if (flowName === FLOWS.SEEKER_CHILD_CARE.name) {
      let selectedCareKind: CareKind = careKindIntentMap[careKind as CareKindIntent];
      if (selectedCareKind) {
        careKindSupplied = true;
      } else {
        selectedCareKind =
          careKindServiceIdMap[serviceIdForMember as ServiceIdsForMember] || initialState.careKind;
      }
      dispatch({ type: 'setCareKind', careKind: selectedCareKind as CareKind });
    }

    const comesFromSeoPage = fromSeoPage === 'true';
    if (comesFromSeoPage) {
      dispatch({ type: 'setSource', source: TrafficSource.SEO });
    }

    const comesFromSemPage = fromSemPage === 'true';
    if (comesFromSemPage) {
      dispatch({ type: 'setSource', source: TrafficSource.SEM });
    }

    if (source) {
      dispatch({ type: 'setSourceType', sourceType: source as TrafficSourceType });
    }

    if (vertical === VERTICALS_NAMES.SENIOR_CARE) {
      let serviceIdForMemberValue = serviceIdForMember as ServiceIdsForSeniorMember;

      if (serviceIdForMemberValue !== ServiceIdsForSeniorMember.AdultCare) {
        serviceIdForMemberValue = ServiceIdsForSeniorMember.SeniorCare;
      }

      dispatch({
        type: 'setCzenServiceIdForSeniorCareMember',
        serviceIdForMember: serviceIdForMemberValue,
      });
    }

    const comesFromIbLandingPage = ib === 'true';
    if (comesFromIbLandingPage) {
      dispatch({ type: 'setCareKind', careKind: DefaultCareKind.ONE_TIME_BABYSITTERS });
    }

    if (utmCampaign) {
      dispatch({ type: 'setUtmCampaign', utmCampaign: utmCampaign as string });
    }

    if (serviceIdForMember === ServiceIdsForMember.childcareDaycareBackup) {
      dispatch({
        type: 'setCzenServiceIdForMember',
        serviceIdForMember: ServiceIdsForMember.childcareDaycareBackup,
      });
    }

    if (intentSupplied || careKindSupplied || zipcodeSupplied || comesFromIbLandingPage) {
      dispatch({
        type: 'setQueryParamMetadata',
        queryParamMetadata: {
          intentSupplied,
          careKindSupplied,
          zipcodeSupplied,
          comesFromIbLandingPage,
        },
      });
    }
  }, [flowName]);
}
